import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MoreHorizontal, PencilLine, Plus, ScanFace, ShieldCheck, UserCheck } from "lucide-react";
import { AuthImage } from "@/components/AuthImage";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { AppShell } from "@/components/AppShell";
import { api, fmtKLDateTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

const EMPTY_FORM = {
  employeeId: "", fullName: "", email: "", phone: "", role: "staff",
  password: "", consentOnFile: false, consentReference: "", consentDate: "",
  shiftIds: [], locationIds: [],
};

export default function AdminUsersPage() {
  const { t } = useLang();
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  // Supervisors (Batch 7) may only manage STAFF accounts — never delete anyone
  const canManage = (u) => isAdmin || u.role === "staff";
  // Batch 40: supervisors may RENAME fellow supervisors and themselves
  // (admins excluded). Staff rows already have the full edit form.
  const canRename = (u) => !isAdmin && u.role === "supervisor";
  // supervisors punch too: they may enrol fellow supervisors' faces (never admins)
  const canEnrol = (u) => isAdmin || u.role !== "admin";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user object or null (=create)
  const [form, setForm] = useState(EMPTY_FORM);
  const [resetTarget, setResetTarget] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [resetPw, setResetPw] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pdpaView, setPdpaView] = useState(null); // user whose PDPA selfie is shown

  const { data: shiftsData } = useQuery({
    queryKey: ["shifts"],
    queryFn: async () => (await api.get("/shifts")).data,
  });

  const { data: locationsData } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations")).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["users", search, roleFilter],
    queryFn: async () => (await api.get("/users", {
      params: { search, role: roleFilter === "all" ? "" : roleFilter },
    })).data,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["users"] });

  const renameMutation = useMutation({
    mutationFn: async () => (await api.patch(`/users/${renameTarget.id}`, { fullName: renameValue.trim() })).data,
    onSuccess: () => {
      toast.success(t("save"));
      setRenameTarget(null);
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        employeeId: form.employeeId, fullName: form.fullName, email: form.email,
        phone: form.phone || null, role: form.role,
        consentOnFile: form.consentOnFile,
        consentReference: form.consentReference || null,
        consentDate: form.consentDate || null,
        shiftIds: form.shiftIds,
        locationIds: form.role === "supervisor" ? form.locationIds : [],
      };
      if (editing) {
        return (await api.patch(`/users/${editing.id}`, payload)).data;
      }
      return (await api.post("/users", { ...payload, password: form.password })).data;
    },
    onSuccess: () => {
      toast.success(t("save"));
      setFormOpen(false);
      invalidate();
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async (u) => (await api.patch(`/users/${u.id}`, { isActive: !u.isActive })).data,
    onSuccess: invalidate,
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  const resetMutation = useMutation({
    mutationFn: async () => (await api.post(`/users/${resetTarget.id}/reset-password`, { newPassword: resetPw })).data,
    onSuccess: () => {
      toast.success(t("resetPassword"));
      setResetTarget(null);
      setResetPw("");
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  const deleteMutation = useMutation({
    // the id is passed at click time: AlertDialogAction closes the dialog and
    // nulls deleteTarget before the mutation function runs (race), so reading
    // deleteTarget.id here would crash and the DELETE was never sent
    mutationFn: async (id) => (await api.delete(`/users/${id}`)).data,
    onSuccess: (res) => {
      toast.success(res.message);
      setDeleteTarget(null);
      invalidate();
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setFormOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      employeeId: u.employeeId, fullName: u.fullName, email: u.email,
      phone: u.phone || "", role: u.role, password: "",
      consentOnFile: u.consentOnFile, consentReference: u.consentReference || "",
      consentDate: u.consentDate || "",
      shiftIds: u.shiftIds || [],
      locationIds: u.locationIds || [],
    });
    setFormOpen(true);
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <AppShell>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-xl font-semibold tracking-tight">{t("users")}</h1>
          <Button onClick={openCreate} data-testid="create-user-button">
            <Plus className="mr-2 h-4 w-4" /> {t("createUser")}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          <Input
            placeholder={t("search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="users-search-input"
            className="h-9 max-w-xs"
          />
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger data-testid="users-role-filter" className="h-9 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allRoles")}</SelectItem>
              <SelectItem value="admin">{t("admin")}</SelectItem>
              <SelectItem value="supervisor">{t("supervisor")}</SelectItem>
              <SelectItem value="staff">{t("staff")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("fullName")}</TableHead>
                <TableHead>{t("employeeId")}</TableHead>
                <TableHead>{t("email")}</TableHead>
                <TableHead>{t("role")}</TableHead>
                <TableHead>{t("status")}</TableHead>
                <TableHead>{t("enrolled")}</TableHead>
                <TableHead>{t("pdpaStatus")}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">{t("loading")}</TableCell></TableRow>
              )}
              {data?.items?.map((u) => (
                <TableRow key={u.id} data-testid="user-row">
                  <TableCell className="font-medium">{u.fullName}</TableCell>
                  <TableCell>{u.employeeId}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{t(u.role)}</TableCell>
                  <TableCell>
                    <Badge variant={u.isActive ? "secondary" : "outline"}>
                      {u.isActive ? t("active") : t("inactive")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {u.enrolled
                      ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><UserCheck className="h-3.5 w-3.5" /> {t("enrolled")}</span>
                      : <span className="text-xs text-muted-foreground">{t("notEnrolledShort")}</span>}
                  </TableCell>
                  <TableCell>
                    {u.pdpaAcceptedAt ? (
                      <button
                        type="button"
                        data-testid="pdpa-view-button"
                        onClick={() => setPdpaView(u)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 underline-offset-2 hover:underline"
                        title={t("viewPdpaSelfie")}
                      >
                        <ShieldCheck className="h-3.5 w-3.5" /> {t("pdpaAccepted")}
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground" data-testid="pdpa-not-accepted">
                        {t("pdpaNotAccepted")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {(canEnrol(u) || canManage(u) || canRename(u)) && (
                    <div className="flex items-center justify-end gap-1">
                      {canRename(u) && (
                      <Button
                        size="sm" variant="outline"
                        data-testid="rename-user-button"
                        onClick={() => { setRenameTarget(u); setRenameValue(u.fullName); }}
                      >
                        <PencilLine className="mr-1.5 h-3.5 w-3.5" />
                        {t("renameUser")}
                      </Button>
                      )}
                      {canEnrol(u) && (
                      <Button
                        size="sm" variant="outline"
                        data-testid="enrol-face-button"
                        onClick={() => navigate(`/admin/users/${u.id}/enrol`)}
                      >
                        <ScanFace className="mr-1.5 h-3.5 w-3.5" />
                        {u.enrolled ? t("reEnrolFace") : t("enrolFace")}
                      </Button>
                      )}
                      {canManage(u) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" data-testid="user-actions-trigger">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(u)} data-testid="user-edit-action">
                            {t("editUser")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setResetTarget(u)} data-testid="user-reset-password-action">
                            {t("resetPassword")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleActiveMutation.mutate(u)} data-testid="user-toggle-active-action">
                            {u.isActive ? t("deactivate") : t("activate")}
                          </DropdownMenuItem>
                          {isAdmin && (
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setDeleteTarget(u)}
                              data-testid="user-delete-action"
                            >
                              {t("deleteUser")}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      )}
                    </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* create / edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent data-testid="user-form-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? t("editUser") : t("createUser")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("employeeId")}</Label>
                <Input value={form.employeeId} onChange={(e) => set("employeeId")(e.target.value)} data-testid="user-form-employee-id" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("role")}</Label>
                {/* Batch 9: supervisors may assign staff or supervisor (never admin) */}
                <Select value={form.role} onValueChange={set("role")}>
                  <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">{t("staff")}</SelectItem>
                    <SelectItem value="supervisor">{t("supervisor")}</SelectItem>
                    {isAdmin && <SelectItem value="admin">{t("admin")}</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("fullName")}</Label>
              <Input value={form.fullName} onChange={(e) => set("fullName")(e.target.value)} data-testid="user-form-full-name" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("email")}</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} data-testid="user-form-email" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("phone")}</Label>
              <Input value={form.phone} onChange={(e) => set("phone")(e.target.value)} data-testid="user-form-phone" />
            </div>
            {!editing && (
              <div className="space-y-1.5">
                <Label>{t("tempPassword")}</Label>
                <Input type="text" value={form.password} onChange={(e) => set("password")(e.target.value)} data-testid="user-form-password" />
                <p className="text-xs text-muted-foreground">{t("passwordRule")}</p>
              </div>
            )}
            {/* HR consent-on-file section removed per user request — the form
                state still carries existing values through saves untouched */}
            <div className="space-y-2 rounded-lg border border-border p-3">
              <Label className="text-sm">{t("assignedShifts")}</Label>
              <p className="text-xs text-muted-foreground">{t("anyShift")}</p>
              {(shiftsData?.items || []).map((s) => (
                <label key={s.id} className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    checked={form.shiftIds.includes(s.id)}
                    data-testid={`user-form-shift-${s.id}`}
                    onCheckedChange={(v) => setForm((f) => ({
                      ...f,
                      shiftIds: v ? [...f.shiftIds, s.id] : f.shiftIds.filter((x) => x !== s.id),
                    }))}
                  />
                  {s.name} <span className="font-mono text-xs text-muted-foreground">{s.startTime}–{s.endTime}</span>
                </label>
              ))}
            </div>
            {form.role === "supervisor" && (
              <div className="space-y-2 rounded-lg border border-border p-3" data-testid="user-form-locations-section">
                <Label className="text-sm">{t("supervisorLocations")}</Label>
                <p className="text-xs text-muted-foreground">{t("supervisorLocationsHint")}</p>
                {(locationsData?.items || []).length === 0 && (
                  <p className="text-xs text-muted-foreground">{t("noLocations")}</p>
                )}
                {(locationsData?.items || []).map((l) => (
                  <label key={l.id} className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.locationIds.includes(l.id)}
                      data-testid={`user-form-location-${l.id}`}
                      onCheckedChange={(v) => setForm((f) => ({
                        ...f,
                        locationIds: v ? [...f.locationIds, l.id] : f.locationIds.filter((x) => x !== l.id),
                      }))}
                    />
                    {l.name}{!l.isActive && <span className="text-xs text-muted-foreground">({t("inactiveLabel")})</span>}
                  </label>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFormOpen(false)}>{t("cancel")}</Button>
            <Button
              data-testid="user-form-save-button"
              disabled={saveMutation.isPending || !form.employeeId || !form.fullName || !form.email || (!editing && form.password.length < 8)}
              onClick={() => saveMutation.mutate()}
            >
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* rename dialog (Batch 40 — supervisors renaming supervisors / themselves) */}
      <Dialog open={renameTarget !== null} onOpenChange={(v) => !v && setRenameTarget(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="rename-user-dialog">
          <DialogHeader>
            <DialogTitle>{t("renameUser")} — {renameTarget?.employeeId}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>{t("fullName")}</Label>
            <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
              maxLength={120} data-testid="rename-user-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameTarget(null)}>{t("cancel")}</Button>
            <Button
              data-testid="rename-user-submit"
              disabled={!renameValue.trim() || renameValue.trim() === renameTarget?.fullName || renameMutation.isPending}
              onClick={() => renameMutation.mutate()}
            >
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* reset password dialog */}
      <Dialog open={resetTarget !== null} onOpenChange={(v) => !v && setResetTarget(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>{t("resetPassword")} — {resetTarget?.fullName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>{t("tempPassword")}</Label>
            <Input value={resetPw} onChange={(e) => setResetPw(e.target.value)} data-testid="reset-password-input" />
            <p className="text-xs text-muted-foreground">{t("passwordRule")}</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetTarget(null)}>{t("cancel")}</Button>
            <Button
              data-testid="reset-password-submit"
              disabled={resetPw.length < 8 || resetMutation.isPending}
              onClick={() => resetMutation.mutate()}
            >
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDPA acceptance selfie viewer */}
      <Dialog open={pdpaView !== null} onOpenChange={(v) => !v && setPdpaView(null)}>
        <DialogContent className="sm:max-w-md" data-testid="pdpa-selfie-dialog">
          <DialogHeader>
            <DialogTitle>{t("viewPdpaSelfie")} — {pdpaView?.fullName}</DialogTitle>
          </DialogHeader>
          {pdpaView && (
            <div className="space-y-3">
              {pdpaView.pdpaSelfiePhotoId ? (
                <AuthImage photoId={pdpaView.pdpaSelfiePhotoId} thumb={false}
                  className="max-h-[55vh] w-full rounded-lg" testId="pdpa-selfie-image" />
              ) : (
                <p className="text-sm text-muted-foreground">{t("photoRemoved")}</p>
              )}
              <p className="text-sm">
                <ShieldCheck className="mr-1 inline h-4 w-4 text-emerald-700" />
                {t("pdpaAcceptedOn")}: <span className="font-medium">{fmtKLDateTime(pdpaView.pdpaAcceptedAt)}</span>
                {pdpaView.pdpaVersion && <span className="text-muted-foreground"> · v{pdpaView.pdpaVersion}</span>}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* delete confirm */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteUser")} — {deleteTarget?.fullName}</AlertDialogTitle>
            <AlertDialogDescription>{t("deleteUserConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-user-confirm-button"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteMutation.mutate(deleteTarget.id)}
            >
              {t("deleteUser")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
